/**
 * File: ProjectContextRequirementGuard.tsx
 * Description: Route guard that requires a BRD upload before workflow progress.
 * Author: GitHub Copilot
 * Created: 2026-06-23
 * Last Modified: 2026-06-23
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { projectService } from '../../services/projectService';
import { hasBusinessRequirementsDocument } from '../../utils/projectContextRequirements';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

interface ProjectContextRequirementGuardProps {
  children: React.ReactElement;
}

/**
 * Protects post-context project sections until the project has an uploaded
 * Business Requirements Document. Other context documents and knowledge-base
 * references are useful, but they do not satisfy this hard workflow gate.
 * @param children - Protected route element
 * @return The protected route, loading state, or redirect to Context Management
 */
export const ProjectContextRequirementGuard: React.FC<
  ProjectContextRequirementGuardProps
> = ({ children }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [isCheckingRequirement, setIsCheckingRequirement] = useState(true);
  const [canProgress, setCanProgress] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkBusinessRequirementsDocument = async (): Promise<void> => {
      if (!projectId) {
        setIsCheckingRequirement(false);
        setCanProgress(false);
        return;
      }

      const projectIdNumber = Number.parseInt(projectId, 10);
      if (Number.isNaN(projectIdNumber)) {
        setIsCheckingRequirement(false);
        setCanProgress(false);
        return;
      }

      setIsCheckingRequirement(true);
      try {
        const contextItems = await projectService.getProjectContext(
          projectIdNumber,
        );
        if (isMounted) {
          setCanProgress(hasBusinessRequirementsDocument(contextItems));
        }
      } catch {
        if (isMounted) {
          setCanProgress(false);
        }
      } finally {
        if (isMounted) {
          setIsCheckingRequirement(false);
        }
      }
    };

    void checkBusinessRequirementsDocument();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  if (isCheckingRequirement) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner label="Checking required project context..." />
      </div>
    );
  }

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  if (!canProgress) {
    return <Navigate to={`/project/${projectId}/context`} replace />;
  }

  return children;
};